import React, { useEffect, useCallback, useState, useImperativeHandle } from "react";
import ReactDOM from "react-dom";
import { SettingRow } from "../../components/organisms/settings/rows/SettingRow";
import { Form, useField } from "react-final-form";
import { Text } from "../../../common/components/input/Text";
import { Select, SelectOption } from "../../../common/components/input/Select";
import { Alert, Tooltip } from "@mui/material";
import { Api } from "../../../common/service/Api";
import { useActionRequest } from "../../../common/hooks/api";
import { Error as OrganizationError } from "../../copy/organization";
import { usePrivacyCenterFonts } from "../../hooks/usePrivacyCenter";
import {
  PrivacyCenterDto,
  PrivacyCenterFontDto,
  UpdatePrivacyCenter,
} from "../../../common/service/server/dto/PrivacyCenterDto";
import { Routes } from "../../root/routes";
import { Upload } from "@mui/icons-material";
import { FontUploadDialog } from "../../components/dialog/FontUploadDialog";
import { requiredHexColor } from "../../util/validation";
import { PrivacyCenterTabsNew } from "../../pages/stay-compliant/privacy-center/PrivacyCenterTabsNew";
import { PrintPreviewPublishButtons } from "../../pages/stay-compliant/privacy-center/us-state-notices/USStateNoticesPage";
import { Breadcrumbs } from "@truevault/aquilo";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../../pages/stay-compliant/PrivacyNoticeChecklistItems";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { didFinishPoliciesAndNoticesOnboarding } from "../../../common/hooks/useOnboardingProgress";

type FontType = "main" | "body" | "navigation" | "section";

type FormValues = {
  activeColor: string;
  inactiveColor: string;
  backgroundColor: string;
  mainHeaderFontFamily: string;
  mainHeaderFontSize: string;
  mainHeaderFontId: string;
  bodyFontFamily: string;
  bodyFontSize: string;
  bodyFontId: string;
  navigationFontFamily: string;
  navigationFontSize: string;
  navigationFontId: string;
  sectionHeaderFontFamily: string;
  sectionHeaderFontSize: string;
  sectionHeaderFontId: string;
};

const DEFAULT_FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 60, 72];

const convertSizeKeysToInts = (values: any) => {
  return Object.keys(values).reduce((acc, key) => {
    if (key.endsWith("Size")) {
      let int = parseInt(values[key]);
      if (Number.isNaN(int)) {
        int = 0;
      }
      acc[key] = int;
    } else {
      acc[key] = values[key];
    }
    return acc;
  }, {} as UpdatePrivacyCenter);
};

export interface PrivacyCenterThemeFormHandle {
  submitForm: () => Promise<void>;
}

interface PrivacyCenterThemeFormProps {
  organizationId: string;
  privacyCenter: PrivacyCenterDto;
  onUpdate?: () => void;
  includeUiShell: boolean;
  draftNoticeId?: string;
  hasPublishedNotice: boolean;
  showTopBannerForUnpublishedChanges?: boolean; // Whether to show a top banner when there are unpublished changes
  setShowingCustomBanner: (value: boolean) => void; // Lets the root <LoggedInApp> component know that we're showing a custom banner, if/when we decide to show a banner
  onPublishNotice?: () => Promise<void>; // Called when publishing to also publish the notice (in addition to theme changes)
}

export const PrivacyCenterThemeForm = React.forwardRef<
  PrivacyCenterThemeFormHandle,
  PrivacyCenterThemeFormProps
>(
  (
    {
      organizationId,
      privacyCenter,
      onUpdate,
      includeUiShell,
      draftNoticeId,
      hasPublishedNotice,
      showTopBannerForUnpublishedChanges = true,
      setShowingCustomBanner,
      onPublishNotice,
    },
    ref,
  ) => {
    const [privacyCenterFonts, , fetchFonts] = usePrivacyCenterFonts(
      organizationId,
      privacyCenter?.id,
    );

    const [saveSuccess, setSaveSuccess] = useState(false);

    // eslint-disable-next-line no-undef
    const [previewWindowRef, setPreviewWindowRef] = useState<WindowProxy | null>(null);

    const updatePrivacyCenterApi = useCallback(
      (submitValues) =>
        Api.privacyCenter.updatePrivacyCenter(
          organizationId,
          privacyCenter.id,
          convertSizeKeysToInts({
            ...submitValues,
          }),
        ),
      [organizationId, privacyCenter?.id],
    );

    const { fetch: updatePrivacyCenterValues } = useActionRequest({
      api: updatePrivacyCenterApi,
      messages: {
        forceError: OrganizationError.UpdateOrganization,
      },
      onSuccess: () => {
        if (onUpdate) {
          onUpdate();
        }
        setSaveSuccess(true);
        setTimeout(() => {
          setSaveSuccess(false);
        }, 5000);
      },
    });

    const openPreviewPopup = (values) => {
      // We can't open the preview until we've finished loading all fonts
      if (!privacyCenterFonts || privacyCenterFonts.length == 0) return;

      let previewActiveColor = encodeURIComponent(values.activeColor);
      let previewInactiveColor = encodeURIComponent(values.inactiveColor);
      let previewBackgroundColor = encodeURIComponent(values.backgroundColor);

      let mainHeaderFont = privacyCenterFonts.find((f) => f.id == values.mainHeaderFontId);
      let previewMainHeaderFont = encodeURIComponent(
        mainHeaderFont.isCustom ? mainHeaderFont.url : mainHeaderFont.name,
      );
      let previewMainHeaderFontSize = values.mainHeaderFontSize.toString();

      let bodyFont = privacyCenterFonts.find((f) => f.id == values.bodyFontId);
      let previewBodyFont = encodeURIComponent(bodyFont.isCustom ? bodyFont.url : bodyFont.name);
      let previewBodyFontSize = encodeURIComponent(values.bodyFontSize);

      let navFont = privacyCenterFonts.find((f) => f.id == values.navigationFontId);
      let previewNavFont = encodeURIComponent(navFont.isCustom ? navFont.url : navFont.name);
      let previewNavFontSize = encodeURIComponent(values.navigationFontSize);

      let sectionFont = privacyCenterFonts.find((f) => f.id == values.sectionHeaderFontId);
      let previewSectionFont = encodeURIComponent(
        sectionFont.isCustom ? sectionFont.url : sectionFont.name,
      );
      let previewSectionFontSize = encodeURIComponent(values.sectionHeaderFontSize);
      let previewThemeQueryParams = `activeColor=${previewActiveColor}&inactiveColor=${previewInactiveColor}&backgroundColor=${previewBackgroundColor}&mainFont=${previewMainHeaderFont}&mainFontSize=${previewMainHeaderFontSize}&bodyFont=${previewBodyFont}&bodyFontSize=${previewBodyFontSize}&navFont=${previewNavFont}&navFontSize=${previewNavFontSize}&sectionFont=${previewSectionFont}&sectionFontSize=${previewSectionFontSize}`;
      let previewWindowUrl = Routes.privacyCenter.previewPopup + "?" + previewThemeQueryParams;
      if (previewWindowRef && !previewWindowRef.closed) {
        previewWindowRef.location.href = previewWindowUrl;
      } else {
        setPreviewWindowRef(
          window.open(previewWindowUrl, "_blank", "toolbar=0,location=0,width=1200,height=900"),
        );
      }
    };

    const colorText =
      "Select the colors for your Privacy Center. Primary is for active selection, secondary color is for inactive items, and background color is for the background of the Privacy Center.";
    const TextThemeText = "Select the fonts for your Privacy Center";
    const handleFormSubmit = (values: FormValues) => {
      updatePrivacyCenterValues(values);
    };
    const { fontFamilyOptions, fontSizeOptions } = useFontSelector(
      privacyCenter,
      privacyCenterFonts,
    );

    const [uploadFontDialogOpen, setUploadFontDialogOpen] = useState(false);
    const [uploadFontType, setUploadFontType] = useState<FontType | undefined>(undefined);
    const [fontUploadPreviousValue, setFontUploadPreviousValue] = useState<string | undefined>(
      undefined,
    );

    const defaultPrimaryColor = "#726D83";
    const defaultSecondaryColor = "#E8E5EE";
    const defaultBackgroundColor = "#FFFFFF";
    const defaultFontFamily = "Default";
    const defaultFontSize = "Auto";

    const defaultFont = privacyCenterFonts?.find((f) => f.name === "Roboto");

    let initialFormValues = {
      activeColor: privacyCenter.activeColor || defaultPrimaryColor,
      inactiveColor: privacyCenter.inactiveColor || defaultSecondaryColor,
      backgroundColor: privacyCenter.backgroundColor || defaultBackgroundColor,
      mainHeaderFontFamily: privacyCenter.mainHeaderFontFamily || defaultFontFamily,
      mainHeaderFontSize: privacyCenter.mainHeaderFontSize || defaultFontSize,
      mainHeaderFontId: privacyCenter.mainHeaderFont?.id || defaultFont?.id || "",
      bodyFontFamily: privacyCenter.bodyFontFamily || defaultFontFamily,
      bodyFontSize: privacyCenter.bodyFontSize || "18",
      bodyFontId: privacyCenter.bodyFont?.id || defaultFont?.id || "",
      navigationFontFamily: privacyCenter.navigationFontFamily || defaultFontFamily,
      navigationFontSize: privacyCenter.navigationFontSize || "18",
      navigationFontId: privacyCenter.navigationFont?.id || defaultFont?.id || "",
      sectionHeaderFontFamily: privacyCenter.sectionHeaderFontFamily || defaultFontFamily,
      sectionHeaderFontSize: privacyCenter.sectionHeaderFontSize || "24",
      sectionHeaderFontId: privacyCenter.sectionHeaderFont?.id || defaultFont?.id || "",
    };

    const { answers: developerInstructionsAnswers } = useSurvey(
      organizationId,
      DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
    );

    const submitHandlerRef = React.useRef<(() => Promise<void>) | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        submitForm: async (): Promise<void> => {
          if (submitHandlerRef.current) {
            await submitHandlerRef.current();
          }
          console.error("No submit handler set");
        },
      }),
      [], // No deps since we're using a ref
    );

    return (
      <Form
        initialValues={initialFormValues}
        onSubmit={handleFormSubmit}
        validate={(values) => {
          const errors = {} as any;
          requiredHexColor(values, "activeColor", errors);
          requiredHexColor(values, "inactiveColor", errors);
          requiredHexColor(values, "backgroundColor", errors);
          return errors;
        }}
        render={({ values, handleSubmit, form, dirty, hasValidationErrors }) => {
          // @ts-ignore - we're using a ref to set the submit handler
          submitHandlerRef.current = handleSubmit;

          const formBody = (
            <>
              {!dirty && saveSuccess && (
                <div style={{ backgroundColor: "#E2FDF3", padding: "16px", color: "#00331F" }}>
                  Your changes were successfully published.
                </div>
              )}
              <hr className="my-lg" />
              <SettingRow title="Color Theme" vertical={false} helpText={colorText}>
                <p className="mt-0">
                  <>
                    <Text field="activeColor" label="Primary Color" required />
                    <Text field="inactiveColor" label="Secondary Color" required />
                    <Text field="backgroundColor" label="Background Color" required />
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        form.change("activeColor", defaultPrimaryColor);
                        form.change("inactiveColor", defaultSecondaryColor);
                        form.change("backgroundColor", defaultBackgroundColor);
                      }}
                      href="#"
                    >
                      Reset to Default Colors
                    </a>
                  </>
                </p>
              </SettingRow>
              <hr className="my-lg" />
              <SettingRow
                title="Text Theme"
                vertical={false}
                maxWidthChildren={true}
                helpText={TextThemeText}
              >
                <p className="mt-0 w-100">
                  <FontSelector
                    field="mainHeaderFont"
                    label="Main Header"
                    openUploadFontDialog={(previousValue: string) => {
                      setFontUploadPreviousValue(previousValue);
                      setUploadFontType("main");
                      setUploadFontDialogOpen(true);
                    }}
                    fontFamilyOptions={fontFamilyOptions}
                    fontSizeOptions={fontSizeOptions}
                  />
                  <FontSelector
                    field="bodyFont"
                    label="Body"
                    openUploadFontDialog={(previousValue: string) => {
                      setFontUploadPreviousValue(previousValue);
                      setUploadFontType("body");
                      setUploadFontDialogOpen(true);
                    }}
                    fontFamilyOptions={fontFamilyOptions}
                    fontSizeOptions={fontSizeOptions}
                    enableFontSizeSelect={false}
                  />
                  <FontSelector
                    field="navigationFont"
                    label="Navigation"
                    openUploadFontDialog={(previousValue: string) => {
                      setFontUploadPreviousValue(previousValue);
                      setUploadFontType("navigation");
                      setUploadFontDialogOpen(true);
                    }}
                    fontFamilyOptions={fontFamilyOptions}
                    fontSizeOptions={fontSizeOptions}
                    enableFontSizeSelect={false}
                  />
                  <FontSelector
                    field="sectionHeaderFont"
                    label="Section Header"
                    openUploadFontDialog={(previousValue: string) => {
                      setFontUploadPreviousValue(previousValue);
                      setUploadFontType("section");
                      setUploadFontDialogOpen(true);
                    }}
                    fontFamilyOptions={fontFamilyOptions}
                    fontSizeOptions={fontSizeOptions}
                    enableFontSizeSelect={false}
                  />
                </p>
                <FontUploadDialog
                  organizationId={organizationId}
                  privacyCenterId={privacyCenter.id}
                  open={uploadFontDialogOpen}
                  onClose={() => {
                    setUploadFontDialogOpen(false);
                    if (fontUploadPreviousValue) {
                      switch (uploadFontType) {
                        case "main":
                          form.change("mainHeaderFontFamily", fontUploadPreviousValue);
                          break;
                        case "body":
                          form.change("bodyFontFamily", fontUploadPreviousValue);
                          break;
                        case "section":
                          form.change("sectionHeaderFontFamily", fontUploadPreviousValue);
                          break;
                        case "navigation":
                          form.change("navigationFontFamily", fontUploadPreviousValue);
                          break;
                      }
                      setFontUploadPreviousValue(undefined);
                    }
                  }}
                  onDone={(newFont: PrivacyCenterFontDto) => {
                    fetchFonts().then(() => {
                      if (uploadFontType === "main") {
                        form.change("mainHeaderFontId", newFont.id);
                      } else if (uploadFontType === "body") {
                        form.change("bodyFontId", newFont.id);
                      } else if (uploadFontType === "section") {
                        form.change("sectionHeaderFontId", newFont.id);
                      } else if (uploadFontType === "navigation") {
                        form.change("navigationFontId", newFont.id);
                      }
                      setFontUploadPreviousValue(undefined);
                    });
                  }}
                />
              </SettingRow>
            </>
          );

          if (includeUiShell) {
            return (
              <>
                {showTopBannerForUnpublishedChanges && dirty && !hasValidationErrors && (
                  <UnpublishedThemeChangesBanner
                    setShowingCustomBanner={setShowingCustomBanner}
                    onPublishClick={handleSubmit}
                  />
                )}
                <div style={{ maxWidth: "1175px" }}>
                  <div className="tw-boundary sticky-header">
                    <Breadcrumbs
                      title="Policies & Notices"
                      sourceRoute={Routes.privacyCenter.root}
                    />

                    <PrivacyCenterTabsNew
                      selectedTabKey="theme"
                      enableNextTab={true}
                      enableAllTabs={didFinishPoliciesAndNoticesOnboarding(
                        developerInstructionsAnswers,
                      )}
                      trailingContent={
                        <PrintPreviewPublishButtons
                          orgId={organizationId}
                          privacyCenter={privacyCenter}
                          draftNoticePublicId={draftNoticeId}
                          hasPublishedNotice={hasPublishedNotice}
                          onPreviewClick={() => openPreviewPopup(values)}
                          onPublishClick={async () => {
                            // If there are theme changes, save them first
                            if (dirty) {
                              await handleSubmit();
                            }
                            // Always publish the notice when clicking Publish
                            if (onPublishNotice) {
                              await onPublishNotice();
                            }
                          }}
                          isPublishEnabled={dirty || !hasPublishedNotice}
                          previewButtonText={
                            previewWindowRef && !previewWindowRef.closed && dirty
                              ? "Update Preview"
                              : "Preview"
                          }
                        />
                      }
                    />
                  </div>
                  <div className="text-t3 mb-xl privacy-center-settings">
                    On this page you can make changes to your hosted Privacy Center so that it
                    matches the look and the practices of your business.
                  </div>
                  {formBody}
                </div>
              </>
            );
          } else {
            return (
              <>
                {showTopBannerForUnpublishedChanges && dirty && !hasValidationErrors && (
                  <UnpublishedThemeChangesBanner
                    setShowingCustomBanner={setShowingCustomBanner}
                    onPublishClick={handleSubmit}
                  />
                )}
                {formBody}
              </>
            );
          }
        }}
      />
    );
  },
);

const UnpublishedThemeChangesBanner = ({
  setShowingCustomBanner,
  onPublishClick,
}: {
  setShowingCustomBanner: (value: boolean) => void;
  onPublishClick: () => void;
}) => {
  const mount = document.getElementById("top-banner");

  useEffect(() => {
    // Let the root <LoggedInApp> component know that we're showing a custom banner
    setShowingCustomBanner(true);
  }, []);

  return ReactDOM.createPortal(
    <Alert
      severity="info"
      icon={false}
      sx={{
        backgroundColor: "#F4F7FF",
        color: "#006296",
        fontWeight: 500,
        display: "block",
        padding: "0",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 24px",
        }}
      >
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <div style={{ alignSelf: "flex-start", height: "24px" }}>
            <ExclamationCircleIcon style={{ width: "24px", height: "24px", color: "#2DA0DD" }} />
          </div>
          <div>
            You have unpublished changes. If you leave this page without publishing, they will not
            be saved.
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <div
            onClick={onPublishClick}
            style={{
              background: "#007BBD",
              border: "none",
              borderRadius: "6px",
              color: "#F9F8FB",
              cursor: "pointer",
              fontWeight: 500,
              padding: "8px 17px",
            }}
          >
            Publish
          </div>
        </div>
      </div>
    </Alert>,
    mount,
  );
};

const useFontSelector = (
  privacyCenter: PrivacyCenterDto,
  privacyCenterFonts: PrivacyCenterFontDto[],
  sizes: number[] = DEFAULT_FONT_SIZES,
): {
  fontFamilyOptions: SelectOption[];
  fontSizeOptions: SelectOption[];
} => {
  useEffect(() => {
    if (!privacyCenterFonts || privacyCenterFonts.length === 0) return;

    // Load custom fonts
    privacyCenterFonts
      .filter((pcf) => pcf.isCustom === true)
      .forEach((pcf) => {
        loadCustomFontFromURL(document, pcf.url);
      });

    // Load default fonts from google fonts
    const defaultFontNames = privacyCenterFonts
      .filter((pcf) => pcf.isCustom === false)
      .map((pcf) => pcf.name);
    const fontStr = defaultFontNames
      .map((fontName) => `family=${encodeURIComponent(fontName)}`)
      .join("&");
    const css = `@import url('https://fonts.googleapis.com/css2?${fontStr}&display=swap');`;
    const style = document.createElement("style");
    style.innerHTML = css;
    document.head.appendChild(style);
  }, [privacyCenterFonts]);

  if (!privacyCenterFonts || privacyCenterFonts.length === 0)
    return { fontFamilyOptions: [], fontSizeOptions: [] };

  const fontSizeOptions = [
    {
      value: "Auto",
      label: "Auto",
      divider: true,
    },
    ...sizes.map((size) => ({
      value: size.toString(),
      label: size.toString(),
    })),
  ];

  const privacyCenterFontsSortedByName = privacyCenterFonts.sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  const fontFamilyOptions = [
    ...privacyCenterFontsSortedByName.map((fontDto, idx) => ({
      value: fontDto.id,
      label: (
        <span
          style={{
            fontFamily: fontDto.name,
          }}
        >
          {fontDto.name}
        </span>
      ),
      ...(idx == privacyCenterFonts.length - 1 ? { divider: true } : {}),
    })),
    {
      value: "_upload",
      label: (
        <div className="flex--between d-flex w-100 align-items-center">
          <span style={{ color: "#007BBD" }}>Upload your font</span>
          <span style={{ paddingTop: "3px" }}>
            <Upload sx={{ color: "#007BBD" }} />
          </span>
        </div>
      ),
    },
  ];

  return { fontFamilyOptions, fontSizeOptions };
};

const FontSelector = ({
  field,
  label,
  fontFamilyOptions,
  fontSizeOptions,
  openUploadFontDialog,
  enableFontSizeSelect = true,
}: {
  field: string;
  label: string;
  fontFamilyOptions: SelectOption[];
  fontSizeOptions: SelectOption[];
  openUploadFontDialog: (previousValue: string) => void;
  enableFontSizeSelect?: boolean;
}) => {
  const fontFamilyFieldName = `${field}Id`;
  const fontSizeFieldName = `${field}Size`;

  const fontFamilyField = useField(fontFamilyFieldName);

  let fontSizeSelectElem = (
    <Select
      disabled={!enableFontSizeSelect}
      field={fontSizeFieldName}
      label="Size"
      cls="w-128"
      options={fontSizeOptions}
      required
    />
  );

  return (
    <div className="fill justify-space-between">
      <Select
        field={fontFamilyFieldName}
        label={label}
        cls="w-100 mr-md"
        options={fontFamilyOptions}
        onChange={(e) => {
          if (e.target.value === "_upload") {
            openUploadFontDialog(fontFamilyField.input.value);
          }
        }}
        required
      />
      {enableFontSizeSelect ? (
        fontSizeSelectElem
      ) : (
        <Tooltip
          title="We've picked an ideal font size for you based on your font selection."
          followCursor={true}
        >
          <span>{fontSizeSelectElem}</span>
        </Tooltip>
      )}
    </div>
  );
};

const getFilename = (fullFilename: string): string => {
  var parts = fullFilename.split(".");
  if (parts.length === 1 || fullFilename.startsWith(".")) {
    return fullFilename;
  }
  parts.pop();
  return parts.join(".");
};

const loadCustomFontFromURL = (document: Document, fontUrl: string): string => {
  let fontName = getFilename(fontUrl.split("/").at(-1)!);
  const style = document.createElement("style");
  // Note the inner single-quotes in case e.g. the font url has whitespace
  style.innerHTML = `@font-face { font-family: ${fontName}; src: url('${fontUrl}') }`;
  document.head.appendChild(style);
  return fontName;
};
